import {
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  ChangeDetectionStrategy,
} from '@angular/core';

import { BehaviorSubject, combineLatest, map, Observable, of, Subscription, take } from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { Category } from '@fcom/dapi/api/models/category';
import { FinnairBoundItem } from '@fcom/dapi/api/models/finnair-bound-item';
import { FinnairCart } from '@fcom/dapi/api/models/finnair-cart';
import { FinnairItineraryItemFlight } from '@fcom/dapi/api/models/finnair-itinerary-item-flight';
import { FinnairOrder } from '@fcom/dapi/api/models/finnair-order';
import { FinnairPassengerItem } from '@fcom/dapi/api/models/finnair-passenger-item';
import { FinnairServiceItem } from '@fcom/dapi/api/models/finnair-service-item';
import { ElementActions, ElementTypes, GtmPurchaseFlow } from '@fcom/common/interfaces/gtm.interface';
import { Amount } from '@fcom/dapi/interfaces/dapi.interface';
import { LocationInfoMap } from '@fcom/dapi/interfaces/itinerary.interface';
import { unsubscribe } from '@fcom/core/utils/unsubscribe';
import { ButtonPosition } from '@fcom/ui-components/components/buttons/cta-button/enums';
import { ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';
import { BookingAppState } from '@fcom/common/interfaces/booking/booking-state.interface';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { finShare } from '@fcom/rx/operators/finShare';
import { SeatMapStatus } from '@fcom/common/interfaces/seat-map.interface';
import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { selectCartFlightTotalPrice } from '@fcom/booking/store/selectors/cart.selector';

import { nonSeatAncillarySubTotalForJourneySelector } from '../../../../../store/selectors/ancillary-sub-total.selector';
import { sumAmounts } from '../../../../../utils/amount.utils';
import { EligibilityWithCategory, ItineraryModal, SeatChangeEvent } from '../../../interfaces/ancillaries-interface';
import * as SeatMapActions from '../../../../../store/actions/seat-map.actions';
import { seatSelections, isSeatsDirty } from '../../../../../store/selectors/seat-map.selector';
import { getSeatMapFlightInfoFromCart } from '../../../utils/ancillary.utils';
import { getBoundDetailsModalData } from '../../../../../utils/ancillaries.utils';
import { AncillaryLoaderStatus } from '../../../enums';
import { ShoppingCartService } from '../../../../../services/shopping-cart.service';

@Component({
  selector: 'fin-seat-pre-selection',
  templateUrl: './seat-pre-selection.component.html',
  styleUrls: ['./seat-pre-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SeatPreSelectionComponent implements OnInit, OnDestroy {
  private store$ = inject<Store<BookingAppState>>(Store);
  private gtmService = inject(GtmService);
  private shoppingCartService = inject(ShoppingCartService);
  private mediaQueryService = inject(MediaQueryService);

  readonly Category = Category;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonPosition = ButtonPosition;
  readonly SeatMapStatus = SeatMapStatus;

  @Input() flights$: Observable<FinnairItineraryItemFlight[]>;
  travelers = input.required<FinnairPassengerItem[]>();
  purchaseFlow = input.required<GtmPurchaseFlow>();
  @Input() bounds$: Observable<FinnairBoundItem[]>;
  @Input() locations: LocationInfoMap = {};
  @Input()
  flightAncillaryAvailability$: Observable<Record<string, EligibilityWithCategory[]>>;
  @Input() cartOrOrder$: Observable<FinnairCart | FinnairOrder>;
  @Input() combinedSeats: FinnairServiceItem | null;
  @Input() selectionLoading$: Observable<boolean>;
  totalPoints = input<number>();

  @Output() preSelectClosed = new EventEmitter<boolean>();
  @Output() flightSelected = new EventEmitter<FinnairItineraryItemFlight>();
  @Output() retrySeatmap = new EventEmitter<FinnairItineraryItemFlight>();
  @Output() seatChanged = new EventEmitter<SeatChangeEvent>();

  slideIndex$ = new BehaviorSubject(0);
  hasSelections$: Observable<boolean>;
  totalPrice$: Observable<Amount>;
  subscriptions: Subscription = new Subscription();
  itineraryModal: ItineraryModal = {
    open: false,
    data: null,
  };
  translationKey$: Observable<{ key?: string; params?: Record<string, string | number> }>;
  passengers$: Observable<FinnairPassengerItem[]>;
  ancillaryLoader$: BehaviorSubject<AncillaryLoaderStatus> = new BehaviorSubject(AncillaryLoaderStatus.OFF);
  seatMapPreloadingEnabled$: Observable<boolean> = of(false);
  isMobile: Signal<boolean>;
  shoppingCartEnabled: boolean;
  readonly showDesktopCart = this.shoppingCartService.showDesktopCart;

  ngOnInit(): void {
    this.seatMapPreloadingEnabled$ = this.cartOrOrder$.pipe(
      map((cartOrOrder) => cartOrOrder.otherInformation.useSeatmapPrefetch),
      finShare()
    );
    this.passengers$ = this.cartOrOrder$.pipe(map((cartOrOrder) => cartOrOrder.passengers));

    this.shoppingCartEnabled = this.shoppingCartService.isEnabled();
    this.isMobile = this.mediaQueryService.isBreakpoint('tablet_down');

    this.totalPrice$ = combineLatest([
      this.store$.select(selectCartFlightTotalPrice),
      this.store$.select(nonSeatAncillarySubTotalForJourneySelector(Category.SEAT)),
    ]).pipe(map(([totalPrice, subTotalPrice]) => sumAmounts(totalPrice, subTotalPrice)));

    this.hasSelections$ = this.store$.select(seatSelections).pipe(
      map((selections) => Object.values(selections).some((selection) => selection.some((s) => s.seatNumber))),
      finShare()
    );

    this.subscriptions.add(
      combineLatest([this.slideIndex$, this.flights$])
        .pipe(
          map(([slideIndex, flights]) => {
            return flights[slideIndex];
          })
        )
        .subscribe((currentFlight) => {
          this.flightSelected.emit(currentFlight);
        })
    );

    this.translationKey$ = combineLatest([this.slideIndex$, this.flights$, this.hasSelections$]).pipe(
      map(([slideIndex, flights, hasSelections]) => {
        const isLast = slideIndex === flights.length - 1;
        if (hasSelections) {
          return isLast ? { key: 'actions.next' } : { key: 'ancillaries.seats.continueToFlight' };
        } else {
          return isLast ? { key: 'ancillaries.seats.skipSeatSelection' } : { key: 'ancillaries.seats.skipFlight' };
        }
      })
    );
  }

  continue(): void {
    this.subscriptions.add(
      combineLatest([this.slideIndex$, this.flights$, this.hasSelections$])
        .pipe(take(1))
        .subscribe(([slideIndex, flights, hasSelections]) => {
          const isLast = slideIndex === flights.length - 1;

          if (hasSelections) {
            this.trackEvent('continue-selection');
          }

          if (!isLast) {
            this.slideIndex$.next(slideIndex + 1);
          } else {
            this.confirmSelection();
          }

          this.trackEvent('skip-selection-bottom', 'bottom');
        })
    );
  }

  confirmSelection(): void {
    this.trackEvent('confirm-selection');
    // setTimeout so that sidebar is closed properly before this component is destroyed
    setTimeout(() => {
      this.preSelectClosed.emit(true);
    });
  }

  skipSeatSelection(trackElementState: string): void {
    this.trackEvent(`skip-selection-${trackElementState}`, trackElementState);
    this.subscriptions.add(
      combineLatest([this.hasSelections$, this.store$.select(isSeatsDirty)])
        .pipe(take(1))
        .subscribe(([hasSelections, seatsDirty]) => {
          if (hasSelections && seatsDirty) {
            this.store$.dispatch(SeatMapActions.clearSelections());
          }
          this.preSelectClosed.emit(seatsDirty);
        })
    );
  }

  changeSeats(e: SeatChangeEvent): void {
    this.seatChanged.emit(e);
  }

  viewDetails(): void {
    this.itineraryModal = getBoundDetailsModalData(this.cartOrOrder$, this.slideIndex$, this.flights$);
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  private trackEvent(elementName: string, state?: string): void {
    this.subscriptions.add(
      this.cartOrOrder$.pipe(take(1)).subscribe((cart) => {
        this.gtmService.seatMapEvent(elementName, ElementTypes.BUTTON, state, ElementActions.CLICK, {
          flightInfo: getSeatMapFlightInfoFromCart(cart),
        });
      })
    );
  }

  /**
   * This is a performance improvement and stops child components being destroyed and recreated when the flights array changes.
   */
  flightsTrackBy(_index: number, flight: FinnairItineraryItemFlight): string {
    return flight.id;
  }

  openShoppingCart(): void {
    this.shoppingCartService.setVisible(true);
  }

  protected readonly SvgLibraryIcon = SvgLibraryIcon;
}
